import { Qual } from 'types';

export const REDACTED = '[REDACTED]';

/**
 * Qual keys that are safe to send to eventstream as-is. Anything not listed
 * here is PII — `userFirstName`, `userLastName`, `userEmail`,
 * `userPhoneNumber`, `phoneNumber` — or a key we don't know about yet.
 *
 * This is an allowlist rather than a list of PII keys on purpose: the qual is
 * partner-supplied JSON decoded straight off the URL, so it can carry keys the
 * `Qual` type has never heard of. Those default to redacted until someone
 * decides they're safe and adds them here.
 *
 * The address is allowed because sending it is the whole point of the
 * `qual` / `loaded` event (DCP-4628). `postalCode` and `apartment` are not on
 * `Qual` but are written by `qualify.tsx`'s normalization.
 */
const NON_PII_QUAL_KEYS = new Set<string>([
  'promoId',
  'street',
  'apartment',
  'unit',
  'line2Type',
  'city',
  'state',
  'zip',
  'postalCode',
  'buyflowType',
  'flowType',
  'waitPageType',
  'gtmCode',
  'sessionId',
  'theme',
  'provider',
  'microsite',
  'optionalFields',
  'uuid',
  'userMoveDate',
]);

const isPresent = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

/**
 * Returns a copy of the qual that is safe to put in an analytics payload:
 * allowlisted keys keep their value, and every other key the partner sent is
 * kept but its value replaced with `REDACTED`. Keeping the redacted keys tells
 * us *that* a partner sent an email or name without recording what it was.
 * Empty values are dropped so "redacted" never stands in for "not sent".
 */
export function redactQual(qual: Qual | undefined): Record<string, unknown> {
  return Object.entries(qual ?? {}).reduce<Record<string, unknown>>(
    (redacted, [key, value]) => {
      if (!isPresent(value)) {
        return redacted;
      }
      redacted[key] = NON_PII_QUAL_KEYS.has(key) ? value : REDACTED;
      return redacted;
    },
    {}
  );
}
